import { useState } from 'react';
import { Trans } from 'react-i18next';

import { loaders } from 'entities';

import { Alert, Checkbox, Modal, Column, Row, Button, Label } from 'components/Blocks';
import { PropertyWithIcon } from 'components/PropertyWithIcon';
import { ButtonGroup } from 'components/ButtonGroup';

import { ArrowRightIcon, CheckIcon, CircleXIcon, InfoIcon, WarningIcon } from 'components/Icons';

import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';
import { useLang, useToggleV2 } from 'hooks';
import { useProjectCancelation } from './cancelProject.hook';

import { ProjectOptionOverlayProps } from '../createProjectOption';

type ACTION = 'RESTORE' | 'PRESERVE' | 'ZERO';

const calculator: Record<ACTION, (waves: number) => number> = {
  PRESERVE: (waves) => waves,

  RESTORE: (waves) => waves + 1,

  ZERO: () => 0,
};

export function TrackerCancelationModal({ close, isOpen }: ProjectOptionOverlayProps) {
  const { t } = useLang();
  const { project } = useProjectProfile();
  const { startCancelation } = useProjectCancelation();

  const [sequenceAction, setSequenceAction] = useState<ACTION | null>(null);

  const [acknowledged, acknowledgedHandlers] = useToggleV2();

  const isSequence = project.type === 'TRACKER_SEQUENCE';

  const { data: originProject } = loaders.projects.fullProject.use({
    blockLoad: !isOpen || !isSequence,
    params: { id: project.originProject! },
  });

  const params = isSequence && sequenceAction ? { sequenceAction } : undefined;

  const buttonDisabled = isSequence
    ? [!sequenceAction, !acknowledged, !originProject].some(Boolean)
    : false;

  const executeAction = (): void => {
    if (buttonDisabled) return;

    startCancelation(params);
    close();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleActionChange = (value: ACTION): void => {
    if (value) setSequenceAction(value);

    acknowledgedHandlers.turnOff();
  };

  const newWaves = sequenceAction ? calculator[sequenceAction](originProject?.wavesLeft ?? 0) : 0;

  return (
    <Modal isOpen={isOpen} close={close} title={t('tracker-cancelation')} width="min(80vw, 650px)">
      <Row justifyContent="center">
        <WarningIcon color="primary" size={40} />

        <h5 className="text-primary">{t('warning')}</h5>
      </Row>

      {!isSequence ? (
        <>
          <p className="text-justify">{t('this-is-a-origin')}</p>

          <p className="text-justify">{t('canceling-this-project-will-origin')}</p>

          <PropertyWithIcon
            icon={<InfoIcon />}
            name={t('number-of-waves')}
            value={`${project.waves}`}
          />

          <PropertyWithIcon
            icon={<InfoIcon />}
            name={t('waves-left')}
            value={`${project.wavesLeft}`}
          />

          <p className="text-center font-semibold">{t('are-you-sure-you-want-to-proceed')}</p>
        </>
      ) : (
        <>
          <p className="text-justify">{t('this-is-a-sequence')}</p>

          <Row justifyContent="center">
            <ButtonGroup
              containerClassName="grid grid-cols-3"
              onChange={handleActionChange}
              value={sequenceAction}
              options={[
                { value: 'RESTORE', label: t('RESTORE') },
                { value: 'PRESERVE', label: t('PRESERVE') },
                { value: 'ZERO', label: t('ZERO') },
              ]}
            />
          </Row>

          {sequenceAction && (
            <Column alignItems="center">
              <Alert level="info" type="lightly-filled">
                <Trans i18nKey={`${sequenceAction}-description`} />
              </Alert>

              <Column>
                <Row gap="2">
                  <span>{t('origin')}:</span>

                  <Button
                    to="projectProfile"
                    variant="link"
                    linkTarget={project.originProject}
                    params={{ projectId: project.originProject! }}
                  >
                    {originProject?.name ?? project.originProject}
                  </Button>
                </Row>

                <PropertyWithIcon
                  icon={<InfoIcon />}
                  name={t('number-of-waves')}
                  addColon
                  value={`${originProject?.waves}`}
                />

                <PropertyWithIcon
                  icon={<InfoIcon />}
                  name={t('waves-left')}
                  addColon
                  value={
                    <Row>
                      <strong>{originProject?.wavesLeft}</strong>

                      <ArrowRightIcon color="primary" />

                      <span className="font-semibold text-primary">{newWaves}</span>
                    </Row>
                  }
                />
              </Column>

              <Row gap="2">
                <Checkbox checked={acknowledged} onChange={acknowledgedHandlers.toggle} />

                <Label>{t('i-understand')}</Label>
              </Row>
            </Column>
          )}
        </>
      )}

      <Row justifyContent="space-between">
        <Button onClick={close} variant="outlined">
          <CircleXIcon />
          {t('cancel')}
        </Button>

        <Button disabled={buttonDisabled} onClick={executeAction}>
          <CheckIcon />
          {t('confirm')}
        </Button>
      </Row>
    </Modal>
  );
}
